import { useSelector, useDispatch } from 'react-redux';
import { Redux } from 'stores/interfaces';
import * as middleware from './middleware';
import { InterceptDistributorRemovalProps, PosReducer, PosParams, HandleSetSelectedPosProps } from './interfaces';
import useDialog from 'stores/dialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleSetFidelityData } from 'stores/fidelity/middleware';
import { DistributorsSourceParams_DEPRECATED } from 'pages/pos/Distributors/interfaces';
import useBasket from 'stores/basket';
import { CustomError } from 'shared/errors';
import { setupPartnerPosFlow } from 'services/pos/partner';
import useCustomer from 'stores/customer';
import { setLogout } from 'stores/customer/actions';

export default function usePos() {
	const dispatch = useDispatch();
	const posStore = useSelector((stores: Redux) => stores.posReducer);

	const navigate = useNavigate();
	const location = useLocation();
	const { handleOpenDialog } = useDialog();
	const { handleGetBasketData, basketStore } = useBasket();
	const {
		customerStore: { provider },
		handleSetToken,
		handleSetLoading,
		handleSetPartnerLabel,
		handleGetCustomerData,
	} = useCustomer();

	const handleSetHasDistributor = (hasDistributor: boolean) => {
		dispatch(middleware.handleSetHasDistributor(hasDistributor));
	};

	const interceptDistributorRemoval = async ({
		distributorId,
		businessUnitId,
		basketReference,
		onConfirm,
		onSeeTheCart,
		onAfterConfirm,
		onAfterRejected,
	}: InterceptDistributorRemovalProps) => {
		await new Promise(async (resolve, reject) => {
			const onResolve = () => resolve({ distributorId, businessUnitId });
			const onReject = () => reject(new CustomError('', 'open-cart'));

			try {
				const baskets = basketReference || basketStore.basket.baskets;

				const hasDistributorInBasket = baskets.some(
					(basket) => basket.businessUnitId === businessUnitId && basket.distributorId === distributorId,
				);

				if (hasDistributorInBasket) {
					await handleOpenDialog({
						heading: {
							title: 'Deseja continuar?',
						},
						content: {
							description:
								'Atenção! Existem produtos em seu carrinho do distribuidor alterado, o mesmo será excluído. Deseja continuar?',
						},
						footer: {
							primaryButton: {
								label: 'Ver carrinho',
								async onClick() {
									onSeeTheCart?.();
									onReject();
								},
							},
							secondaryButton: {
								label: 'Confirmar',
								async onClick() {
									onConfirm?.();
									onResolve();
								},
							},
						},
					});
				}

				onAfterConfirm?.();
				onResolve();
			} catch {
				onAfterRejected?.();
				onReject();
			}
		});
	};

	const handleIncrementToMyPos = (pos: PosReducer['myPos']) => {
		const objectify = (list: PosParams[]) =>
			list.reduce((acc, item) => ({ ...acc, [item.cnpj]: item }), {} as Record<string, PosParams>);

		const currentPos = objectify(posStore.myPos);
		const camePos = objectify(pos);

		const mergedAndUpdated = Object.entries(camePos).reduce(
			(acc, [cnpj, camePosData]) => ({ ...acc, [cnpj]: { ...(acc?.[cnpj] || {}), ...camePosData } }),
			currentPos,
		);

		return dispatch(middleware.handleSetMyPos(Object.values(mergedAndUpdated)));
	};

	const handleSetPosLoading = (posId: PosReducer['loading']) => dispatch(middleware.handleSetPosLoading(posId));

	const handleSelectPos = async ({
		cnpj,
		posId,
		isPartner,
		openSideBasket,
		isDistributorsPage,
		redirectUrl,
	}: Pick<
		HandleSetSelectedPosProps,
		'cnpj' | 'posId' | 'isDistributorsPage' | 'openSideBasket' | 'isPartner' | 'redirectUrl'
	>) => {
		await dispatch(
			middleware.handleSetSelectedPos({
				cnpj,
				posId,
				location,
				isPartner,
				openSideBasket,
				redirectUrl,
				isDistributorsPage,
				navigate,
				handleFidelity: provider === 'PARCEIRO_HYPERA' ? handleSetFidelityData : undefined,
				handleOpenDialog,
				handleGetBasketData,
				handleIncrementToMyPos,
			}),
		);
	};

	const handleSetupPartnerPos = async (token: string, origin = 'PARTNER', ean?: string) => {
		handleSetPosLoading(origin);

		try {
			navigate('/acesso-distribuidor', { replace: true });

			handleSetToken(token, origin);
			await handleGetCustomerData({ ignoreLoading: true });

			const {
				pointOfSale: { id: posId, cnpj },
				integrationPartner: { name: partnerLabel },
			} = await setupPartnerPosFlow();

			handleSetPartnerLabel(partnerLabel);

			await handleSelectPos({ posId, cnpj, isPartner: true });

			if (!ean) return navigate('/');
			return;
		} catch (error) {
			const errorType = (() => {
				if ((error as any).code === 404) return 'no-service';
				if ((error as any).code === 400) return 'unauthorized';

				return undefined;
			})();

			if (errorType) {
				dispatch(setLogout());

				return navigate('/acesso-distribuidor', {
					state: { errorType },
				});
			}

			navigate('/login', {
				replace: true,
				state: {
					logout: true,
					notify: {
						type: 'negative',
						payload: { description: 'Falha ao tentar acessar a farmácia. Tente novamente mais tarde.' },
					},
				},
			});
		} finally {
			handleSetLoading(false);
			handleSetPosLoading(false);
			if (ean) navigate('/busca/' + ean, { state: { homeFallback: true } });
		}
	};

	return {
		posStore,
		selectedPos: posStore.myPos.find(({ pointOfSaleId }) => pointOfSaleId === posStore.selectedPos.id),
		handleSetMyPos: (pos: PosReducer['myPos']) => dispatch(middleware.handleSetMyPos(pos)),
		handleIncrementToMyPos,
		handleSetPosLoading,
		handleCleanPos: () => dispatch(middleware.handleSetCleanPos()),
		handleClearSelectedPos: () => dispatch(middleware.handleClearSelectedPos()),
		handleSetPosDistributors: (distributors: Concrete<PosReducer['selectedPos']['distributors']>) =>
			dispatch(middleware.handleSetPosDistributors(distributors)),
		handleSelectPos,
		handleSetupPartnerPos,
		handleUpdateDistributor: (distributor: DistributorsSourceParams_DEPRECATED) =>
			dispatch(middleware.handleSetUpdateDistributor(distributor)),
		clearSelectedPosIndustryDistributors: () => dispatch(middleware.handleClearSelectedPosIndustryDistributors()),
		handleSetHasDistributor,
		interceptDistributorRemoval,
		handleSetBankExists: (exists: boolean) => dispatch(middleware.handleSetBankExists(exists)),
	};
}
